import React from 'react';
import { useIntl } from 'react-intl';
import SkeletonImage from './common/SkeletonImage';

const ExcellenceCenterSection = ({ centerData }) => {
  const intl = useIntl();

  const {
    title = intl.formatMessage({ id: 'excellence.center.title' }),
    description: rawDescription = '',
    establishmentYear = 2018,
    location = intl.formatMessage({ id: 'excellence.center.location' }),
    partnershipDescription = intl.formatMessage({
      id: 'excellence.center.partnership.description'
    }),
    image,
    universityPartners: rawUniversityPartners = [],
    focusAreas: rawFocusAreas = []
  } = centerData || {};

  const descriptionParts = rawDescription.split('\n\n');
  const description =
    descriptionParts[0] || intl.formatMessage({ id: 'excellence.center.description' });
  const extractedPartnershipDescription = descriptionParts[1] || partnershipDescription;

  const focusAreas = rawFocusAreas.map((area) => area.name);

  const universityPartners = rawUniversityPartners.map((partner) => ({
    name: partner.name,
    type: partner.type || intl.formatMessage({ id: 'academic.partner' })
  }));

  const imageUrl = image
    ? `${image.url}`
    : 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80';

  return (
    <section className="bg-white">
      {/* Image pleine largeur */}
      <div className="relative w-full h-[42vh] min-h-[280px] max-h-[480px] overflow-hidden bg-slate-200">
        <SkeletonImage
          src={imageUrl}
          alt="Centre d'Excellence d'Africa Systems à Dschang"
          className="w-full h-[42vh] min-h-[280px] max-h-[480px]"
          imgClassName="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pb-8 lg:pb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[#16A34A]" />
              <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                Formation
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              {title}
            </h2>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed text-base lg:text-lg mb-6">{description}</p>
            <p className="text-slate-500 leading-relaxed mb-10">{extractedPartnershipDescription}</p>

            <div className="flex flex-col sm:flex-row gap-8 border-t border-slate-200 pt-8">
              <div>
                <div className="text-3xl font-bold text-[#0a1628] tabular-nums">{establishmentYear}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-400">
                  {intl.formatMessage({ id: 'establishment.year' })}
                </div>
              </div>
              <div className="hidden sm:block w-px bg-slate-200" />
              <div>
                <div className="text-lg font-semibold text-[#0a1628]">{location}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-400">
                  {intl.formatMessage({ id: 'location.label' })}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 mb-5">
              {intl.formatMessage({ id: 'expertise.areas' })}
            </h3>
            <ul className="mb-12 space-y-0">
              {focusAreas.map((area, index) => (
                <li
                  key={index}
                  className="flex items-center gap-4 py-3.5 border-b border-slate-100 text-slate-800 font-medium"
                >
                  <span className="text-[11px] text-[#16A34A] tabular-nums tracking-widest">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {area}
                </li>
              ))}
            </ul>

            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 mb-5">
              {intl.formatMessage({ id: 'university.partners' })}
            </h3>
            <div className="space-y-4">
              {universityPartners.map((partner, index) => (
                <div key={index} className="flex items-baseline justify-between gap-4 border-b border-slate-100 pb-4">
                  <div className="font-semibold text-[#0a1628]">{partner.name}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-[0.14em] shrink-0">
                    {partner.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bannière PhD — bloc vert institutionnel */}
        <div className="mt-16 lg:mt-20 bg-[#15803D] text-white px-8 py-10 lg:px-12 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-4">
              <h3 className="text-xl lg:text-2xl font-bold">
                {intl.formatMessage({ id: 'phd.program' })}
              </h3>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-white/80 leading-relaxed">{partnershipDescription}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExcellenceCenterSection;
